import { useCallback, useRef, useEffect } from 'react'
import { useApp } from '@/contexts/AppContext'
import { SSEEventData } from '@/types'
import { SSE_CONFIG } from '@/lib/constants'
import { apiUrl } from '@/lib/api-config'
import { parseReviewCompletePayload, type ReviewStreamCompletion } from '@/lib/contract-review-sse'

/**
 * Hook for handling Server-Sent Events (SSE) streaming
 */
export function useSSE() {
  const { setProgress, addLog, setResults, setError, setViewState, setContractText, setChatThreadId } = useApp()
  const heartbeatTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const currentEventRef = useRef<string>('')

  const resetHeartbeat = useCallback(() => {
    if (heartbeatTimeoutRef.current) {
      clearTimeout(heartbeatTimeoutRef.current)
    }
    heartbeatTimeoutRef.current = setTimeout(() => {
      setProgress({ isConnected: false })
    }, SSE_CONFIG.HEARTBEAT_TIMEOUT)
  }, [setProgress])

  const handleProgress = useCallback((data: SSEEventData) => {
    resetHeartbeat()
    if (data.message) {
      setProgress({
        message: data.message,
        step: data.step,
        progress: data.progress,
        isConnected: true,
      })
      addLog(data.message, data.step)
    } else if (data.progress !== undefined) {
      setProgress({ progress: data.progress, isConnected: true })
    }
  }, [resetHeartbeat, setProgress, addLog])

  const handleHeartbeat = useCallback(() => {
    setProgress({ isConnected: true })
    resetHeartbeat()
  }, [resetHeartbeat, setProgress])

  const handleComplete = useCallback((data: SSEEventData): ReviewStreamCompletion | null => {
    // Clear heartbeat timeout
    if (heartbeatTimeoutRef.current) {
      clearTimeout(heartbeatTimeoutRef.current)
    }

    const parsed = parseReviewCompletePayload(data)
    if (parsed) {
      setResults(parsed.result)
      if (typeof data.contractText === 'string' && data.contractText.length > 0) {
        setContractText(data.contractText)
      }
      if (typeof data.reviewId === 'string' && data.reviewId.length > 0) {
        setChatThreadId(data.reviewId)
      }
      return parsed
    }

    const actualStatus = data.status
    if (actualStatus === 'success' || actualStatus === 'completed' || !actualStatus) {
      setError('Invalid response format: reviewTable is missing. Check server logs.')
      setViewState('empty')
    } else {
      setError('Unexpected result status: ' + actualStatus)
      setViewState('empty')
    }
    return null
  }, [setResults, setError, setViewState, setContractText, setChatThreadId])

  const handleError = useCallback((data: SSEEventData) => {
    if (heartbeatTimeoutRef.current) {
      clearTimeout(heartbeatTimeoutRef.current)
    }
    setError(data.message || data.error || 'An error occurred during processing')
    setViewState('empty')
  }, [setError, setViewState])

  const streamReview = useCallback(async (
    requestData: Record<string, unknown>,
    accessToken?: string | null,
  ): Promise<ReviewStreamCompletion | null> => {
    // Reset state for new request
    currentEventRef.current = ''
    setProgress({
      message: 'Connecting...',
      progress: 0,
      isConnected: false,
      logs: [],
    })

    try {
      const response = await fetch(apiUrl('/review-contract'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify(requestData),
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const reader = response.body?.getReader()
      if (!reader) {
        throw new Error('No response body')
      }

      const decoder = new TextDecoder()
      let buffer = ''
      let completeReceived = false

      let completedResult: ReviewStreamCompletion | null = null
      while (true) {
        const { done, value } = await reader.read()

        if (done) break

        buffer += decoder.decode(value, { stream: true })

        const lines = buffer.split('\n')
        buffer = lines.length > 0 && !buffer.endsWith('\n') ? lines.pop() || '' : ''

        for (const line of lines) {
          const trimmedLine = line.trim()
          if (!trimmedLine) continue

          if (trimmedLine.startsWith('event: ')) {
            currentEventRef.current = trimmedLine.substring(7).trim()
          } else if (trimmedLine.startsWith('data: ')) {
            try {
              const dataStr = trimmedLine.substring(6)
              const data: SSEEventData = JSON.parse(dataStr)
              const eventType = currentEventRef.current || 'message'

              switch (eventType) {
                case 'run-started':
                  // Just log the run ID
                  console.log('Run started:', data.runId)
                  break
                case 'progress':
                  handleProgress(data)
                  break
                case 'heartbeat':
                  handleHeartbeat()
                  break
                case 'complete':
                  completeReceived = true
                  completedResult = handleComplete(data)
                  return completedResult
                case 'error':
                  handleError(data)
                  return null
                case 'message':
                  if (data.status === 'success' && data.result) {
                    completeReceived = true
                    completedResult = handleComplete(data)
                    return completedResult
                  }
                  break
              }
            } catch (parseErr) {
              console.error('Failed to parse SSE data:', trimmedLine, parseErr)
            }
          }
        }
      }

      if (!completeReceived) {
        setError('Connection closed unexpectedly. Please try again.')
        setViewState('empty')
      }
      return completedResult
    } catch (err) {
      console.error('Stream error:', err)
      setError('Failed to connect to the API. Make sure the server is running.')
      setViewState('empty')
      return null
    }
  }, [handleProgress, handleHeartbeat, handleComplete, handleError, setProgress, setError, setViewState])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (heartbeatTimeoutRef.current) {
        clearTimeout(heartbeatTimeoutRef.current)
      }
    }
  }, [])

  return { streamReview }
}
